import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import styles from '../Patient.module.css';
import { Card, Row, Col, Collapse } from 'react-bootstrap';
import useAxios from '../../../services/AxiosConfig';
import { fetchDeviceTrackingData } from '../../../utils/deviceTrackingUtils';
import { getBatteryColor, getBatteryStatusText } from '../../../utils/batteryUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DeviceInfoView = () => {
  const patientInfo = useSelector((state) => state.patient);
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const httpService = useAxios(axiosConfig.envURL);
  const [hasActivePatch, setHasActivePatch] = useState(false);
  const [patchInfo, setPatchInfo] = useState({serialNumber: "Unknown", macAddress: "Unknown"});
  const [deviceData, setDeviceData] = useState(null);
  const [error, setError] = useState(null);
  const [batteryData, setBatteryData] = useState(null);
  const [rawBatteryData, setRawBatteryData] = useState(null); // Store raw API data
  const [signalQualityData, setSignalQualityData] = useState(null);
  const [rawSignalQualityData, setRawSignalQualityData] = useState(null); // Store raw SQI API data
  const [alertsData, setAlertsData] = useState(null); // Store alerts API data
  const [batteryChartView, setBatteryChartView] = useState('hourly'); // 'hourly', 'daily', 'weekly', 'monthly', 'all'
  const [signalQualityChartView, setSignalQualityChartView] = useState('hourly'); // 'hourly', 'daily', 'weekly', 'monthly', 'all'
  const [patchMetadata, setPatchMetadata] = useState(null); // Store patch metadata (hardware/software versions)
  const [latestRecordingTimestamp, setLatestRecordingTimestamp] = useState(null);
  const [isDeviceOnTrack, setIsDeviceOnTrack] = useState(true);
  const [expandedAlerts, setExpandedAlerts] = useState({}); // Track which alerts are expanded
  const lastTrackingCallRef = useRef(null); // Track last API call parameters
  const [rebootCount, setRebootCount] = useState(null);
  const [lastRebootTimestamp, setLastRebootTimestamp] = useState(null);

  useEffect(() => {
    const getPatchInfo = () => {
      const currentPrograms = patientInfo.carePrograms.filter(program => program.enrollment.dischargeDate === null);
      
      if (currentPrograms.length > 0) {
        const perinPatch = currentPrograms[0].devices.find(device => device.deviceId === 1);
        if (perinPatch) {
          setHasActivePatch(true);
          return {serialNumber: perinPatch.serialNumber, macAddress: perinPatch.macAddress, serialNumberId: perinPatch.serialNumberId}
        }
      }
      
      let perinPatch = null;
      for (const program of patientInfo.carePrograms) {
        if (Array.isArray(program.devices)) {
          perinPatch = program.devices.find(device => device.deviceId === 1);
          if (perinPatch) break;
        }
      }
      
      // If we found a patch in the programs, use it
      if (perinPatch) {
        setHasActivePatch(true);
        return {serialNumber: perinPatch.serialNumber, macAddress: perinPatch.macAddress, serialNumberId: perinPatch.serialNumberId};
      }
      
      setHasActivePatch(false);
      
      return {serialNumber: "Unknown", macAddress: "Unknown", serialNumberId: 0}
    };

    const info = getPatchInfo();
    setPatchInfo(info);
  }, [patientInfo.carePrograms]);

  useEffect(() => {
    const fetchDeviceData = async () => {
      // Check if we have both required IDs before making API call
      if (!patchInfo.serialNumberId || patchInfo.serialNumberId === 0 || !patientInfo.id) {
        return;
      }

      try {
        setError(null);
        
        // Fetch device data from the API
        const response = await httpService.get(`/device/all/${patientInfo.id}`);
        
        if (response.data?.data && Array.isArray(response.data.data)) {
          // Find the specific device by serialNumberId
          const device = response.data.data.find(d => d.id === patchInfo.serialNumberId);
          
          if (device) {
            setDeviceData(device);
          } else {
            // If device not found, try to find by serialNumber as fallback
            const deviceBySerial = response.data.data.find(d => d.serialNumber === patchInfo.serialNumber);
            if (deviceBySerial) {
              setDeviceData(deviceBySerial);
            } else {
              // If still not found, use the first device or create a default structure
              const firstDevice = response.data.data[0];
              if (firstDevice) {
                setDeviceData(firstDevice);
              } else {
                // Create a default device structure with null values
                setDeviceData({
                  id: null,
                  serialNumber: null,
                  macAddress: null,
                  isActive: false,  
                  isAssigned: false,
                  patientId: null,
                  dateAssigned: null,
                  lastConnection: null,
                  lastDataTransmissionTime: null,
                  currentSoftwareVersion: null,
                  hardwareVersion: null,
                  initialSoftwareVersion: null,
                  housingVersion: null,
                  electronicsManufacturerName: null,
                  electronicsManufacturerDate: null,
                  electronicsManufacturerBatch: null,
                  plasticsManufacturerName: null,
                  plasticsManufacturerDate: null,
                  plasticsManufacturerBatch: null,
                  assemblyManufacturerName: null,
                  assemblyManufacturerDate: null,
                  assemblyManufacturerBatch: null,
                  batteryLotNumber: null,
                  initialTestingDate: null,
                  checkInOutStatus: null,
                  teamMemberAssigned: null,
                  checkOutReason: null,
                  checkOutDateTime: null,
                  checkInDateTime: null,
                  studyId: null,
                  studySite: null,
                  collectionInfo: null,
                  dataRepo: null,
                  isRefurbished: null,
                  checkOutSoftware: null,
                  organizationName: null,
                  groupName: null,
                  firstName: null,
                  lastName: null
                });
              }
            }
          }
        } else {
          setError('Invalid API response format');
        }
      } catch (err) {
        console.error('Error fetching device data:', err);
        setError('Failed to fetch device data');
      }
    };

    fetchDeviceData();
  }, [patchInfo.serialNumberId, patchInfo.serialNumber, patientInfo.id, httpService]);

  // Process signal quality data based on current view
  const processSignalQualityData = (rawData, view) => {
    if (!rawData || rawData.length === 0) {
      return {
            labels: [],
            datasets: []
      };
    }

    // Sort the data by date to ensure proper chronological order
    const sortedData = rawData.sort((a, b) => new Date(a.completedDate) - new Date(b.completedDate));
    
    // Group data by modality type
    const modalityGroups = {};
    sortedData.forEach(item => {
      const modalityKey = item.modalityType;
      if (!modalityGroups[modalityKey]) {
        modalityGroups[modalityKey] = {
          modalityType: item.modalityType,
          data: []
        };
      }
      modalityGroups[modalityKey].data.push(item);
    });

    // Define colors for different modalities
    const modalityColors = [
      { border: '#28a745', background: 'rgba(40, 167, 69, 0.1)', point: '#28a745' },
      { border: '#007bff', background: 'rgba(0, 123, 255, 0.1)', point: '#007bff' },
      { border: '#dc3545', background: 'rgba(220, 53, 69, 0.1)', point: '#dc3545' },
      { border: '#ffc107', background: 'rgba(255, 193, 7, 0.1)', point: '#ffc107' },
      { border: '#6f42c1', background: 'rgba(111, 66, 193, 0.1)', point: '#6f42c1' },
      { border: '#fd7e14', background: 'rgba(253, 126, 20, 0.1)', point: '#fd7e14' },
      { border: '#20c997', background: 'rgba(32, 201, 151, 0.1)', point: '#20c997' },
      { border: '#e83e8c', background: 'rgba(232, 62, 140, 0.1)', point: '#e83e8c' }
    ];
    
    let chartLabels = [];
    const datasets = [];
    
    // Process each modality
    Object.values(modalityGroups).forEach((modality, index) => {
      const colorIndex = index % modalityColors.length;
      const color = modalityColors[colorIndex];
      
      let chartData = [];
      
      if (view === 'hourly') {
        // For hourly view, show data for the last hour
        const now = new Date();
        const lastHour = new Date(now.getTime() - 60 * 60 * 1000);
        
        // Filter data to last hour
        const recentData = modality.data.filter(item => new Date(item.completedDate) >= lastHour);
        
        // If we have any data, create chart
        if (recentData.length > 0) {
          // Set labels on first iteration
          if (index === 0) {
            chartLabels = recentData.map(item => {
              const date = new Date(item.completedDate);
              return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              });
            });
          }
          
          // Create data from actual readings
          chartData = recentData.map(item => {
            if (modality.modalityType === 'ppg') {
              // For PPG, use sqiIr if available
              if (item.sqiIr !== undefined && item.sqiIr !== null) {
                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                return Math.round(sqiIrValue);
              }
            } else {
              // For other modalities, use sqi field
              if (item.sqi !== undefined && item.sqi !== null) {
                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                return Math.round(sqiValue);
              }
            }
            return null;
          });
        } else {
          // No data in last hour
          if (index === 0) {
            chartLabels = [];
          }
          chartData = [];
        }
        
      } else if (view === 'daily') {
        // For daily view, show hourly data for the last 24 hours
        const now = new Date();
        const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        
        // Filter data to last 24 hours
        const recentData = modality.data.filter(item => new Date(item.completedDate) >= last24Hours);
        
        // Create hourly buckets
        const hourlyData = {};
        for (let i = 0; i < 24; i++) {
          const hour = new Date(now.getTime() - i * 60 * 60 * 1000);
          const hourKey = hour.toLocaleTimeString('en-US', {
            hour: 'numeric',
            hour12: true
          });
          hourlyData[hourKey] = {
            time: hour,
            signalQualityValues: [],
            count: 0
          };
        }
        
        // Group data by hour
        recentData.forEach(item => {
          const itemTime = new Date(item.completedDate);
          const hourKey = itemTime.toLocaleTimeString('en-US', {
            hour: 'numeric',
            hour12: true
          });
          if (hourlyData[hourKey]) {
            if (modality.modalityType === 'ppg') {
              // For PPG, use sqiIr if available, otherwise skip
              if (item.sqiIr !== undefined && item.sqiIr !== null) {
                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                hourlyData[hourKey].signalQualityValues.push(sqiIrValue);
                if (!hourlyData[hourKey].originalValues) {
                  hourlyData[hourKey].originalValues = [];
                }
                hourlyData[hourKey].originalValues.push(item.sqiIr);
                hourlyData[hourKey].count++;
              }
            } else {
              // For other modalities, use sqi field
              if (item.sqi !== undefined && item.sqi !== null) {
                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                hourlyData[hourKey].signalQualityValues.push(sqiValue);
                if (!hourlyData[hourKey].originalValues) {
                  hourlyData[hourKey].originalValues = [];
                }
                hourlyData[hourKey].originalValues.push(item.sqi);
                hourlyData[hourKey].count++;
              }
            }
          }
        });
        
        // Convert to chart arrays (reverse order to show oldest to newest)
        if (index === 0) {
          chartLabels = Object.keys(hourlyData).reverse();
        }
        chartData = chartLabels.map(label => {
          const hourData = hourlyData[label];
          if (hourData && hourData.count > 0) {
            const averageSignalQuality = hourData.signalQualityValues.reduce((sum, level) => sum + level, 0) / hourData.count;
            // Don't mark any modality values as invalid since signal quality values are typically positive
            const hasInvalidReadings = false;
            return {
              value: Math.round(averageSignalQuality),
              hasInvalidReadings: hasInvalidReadings
            };
          }
          return null; // No data for this hour - will show as gap
        });
      
      } else if (view === 'weekly') {
        // For weekly view, show all 7 days including gaps
        const now = new Date();
        const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const recentData = modality.data.filter(item => new Date(item.completedDate) >= last7Days);
        
        // Create daily buckets for all 7 days
        const dailyData = {};
        for (let i = 0; i < 7; i++) {
          const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const dayKey = day.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          dailyData[dayKey] = {
            date: day,
            signalQualityValues: [],
            count: 0
          };
        }
        
        // Group data by day
        recentData.forEach(item => {
          const dateKey = new Date(item.completedDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          
          if (dailyData[dateKey]) {
            if (modality.modalityType === 'ppg') {
              // For PPG, use sqiIr if available, otherwise skip
              if (item.sqiIr !== undefined && item.sqiIr !== null) {
                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                dailyData[dateKey].signalQualityValues.push(sqiIrValue);
                if (!dailyData[dateKey].originalValues) {
                  dailyData[dateKey].originalValues = [];
                }
                dailyData[dateKey].originalValues.push(item.sqiIr);
                dailyData[dateKey].count++;
              }
            } else {
              // For other modalities, use sqi field
              if (item.sqi !== undefined && item.sqi !== null) {
                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                dailyData[dateKey].signalQualityValues.push(sqiValue);
                if (!dailyData[dateKey].originalValues) {
                  dailyData[dateKey].originalValues = [];
                }
                dailyData[dateKey].originalValues.push(item.sqi);
                dailyData[dateKey].count++;
              }
            }
          }
        });
        
        // Convert to chart arrays (reverse order to show oldest to newest)
        if (index === 0) {
          chartLabels = Object.keys(dailyData).reverse();
        }
        chartData = chartLabels.map(label => {
          const dayData = dailyData[label];
          if (dayData && dayData.count > 0) {
            const averageSignalQuality = dayData.signalQualityValues.reduce((sum, level) => sum + level, 0) / dayData.count;
            // Don't mark any modality values as invalid since signal quality values are typically positive
            const hasInvalidReadings = false;
            return {
              value: Math.round(averageSignalQuality),
              hasInvalidReadings: hasInvalidReadings
            };
          }
          return null; // No data for this day - will show as gap
        });
      
      } else if (view === 'monthly') {
        // For monthly view, show all 30 days including gaps
        const now = new Date();
        const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        
        const recentData = modality.data.filter(item => new Date(item.completedDate) >= last30Days);
        
        // Create daily buckets for all 30 days
        const dailyData = {};
        for (let i = 0; i < 30; i++) {
          const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const dayKey = day.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          dailyData[dayKey] = {
            date: day,
            signalQualityValues: [],
            count: 0
          };
        }
        
        // Group data by day
        recentData.forEach(item => {
          const dateKey = new Date(item.completedDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          
          if (dailyData[dateKey]) {
            if (modality.modalityType === 'ppg') {
              // For PPG, use sqiIr if available, otherwise skip
              if (item.sqiIr !== undefined && item.sqiIr !== null) {
                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                dailyData[dateKey].signalQualityValues.push(sqiIrValue);
                if (!dailyData[dateKey].originalValues) {
                  dailyData[dateKey].originalValues = [];
                }
                dailyData[dateKey].originalValues.push(item.sqiIr);
                dailyData[dateKey].count++;
              }
            } else {
              // For other modalities, use sqi field
              if (item.sqi !== undefined && item.sqi !== null) {
                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                dailyData[dateKey].signalQualityValues.push(sqiValue);
                if (!dailyData[dateKey].originalValues) {
                  dailyData[dateKey].originalValues = [];
                }
                dailyData[dateKey].originalValues.push(item.sqi);
                dailyData[dateKey].count++;
              }
            }
          }
        });
        
        // Convert to chart arrays (reverse order to show oldest to newest)
        if (index === 0) {
          chartLabels = Object.keys(dailyData).reverse();
        }
        chartData = chartLabels.map(label => {
          const dayData = dailyData[label];
          if (dayData && dayData.count > 0) {
            const averageSignalQuality = dayData.signalQualityValues.reduce((sum, level) => sum + level, 0) / dayData.count;
            // Don't mark any modality values as invalid since signal quality values are typically positive
            const hasInvalidReadings = false;
            return {
              value: Math.round(averageSignalQuality),
              hasInvalidReadings: hasInvalidReadings
            };
          }
          return null; // No data for this day - will show as gap
        });
        
      } else if (view === 'all') {
        // For all view, show all days from first to last data point including gaps
        if (modality.data.length === 0) {
          chartData = [];
        } else {
          // Find the date range from first to last data point for this modality
          const modalitySortedData = modality.data.sort((a, b) => new Date(a.completedDate) - new Date(b.completedDate));
          const firstDate = new Date(modalitySortedData[0].completedDate);
          const lastDate = new Date(modalitySortedData[modalitySortedData.length - 1].completedDate);
          
          // Create daily buckets for all days in the range
          const dailyData = {};
          const currentDate = new Date(firstDate);
          while (currentDate <= lastDate) {
            const dateKey = currentDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            });
            dailyData[dateKey] = {
              date: new Date(currentDate),
              signalQualityValues: [],
              count: 0
            };
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          // Group data by day
          modalitySortedData.forEach(item => {
            const dateKey = new Date(item.completedDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            });
            
            if (dailyData[dateKey]) {
              if (modality.modalityType === 'ppg') {
                // For PPG, use sqiIr if available, otherwise skip
                if (item.sqiIr !== undefined && item.sqiIr !== null) {
                  const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                  dailyData[dateKey].signalQualityValues.push(sqiIrValue);
                  if (!dailyData[dateKey].originalValues) {
                    dailyData[dateKey].originalValues = [];
                  }
                  dailyData[dateKey].originalValues.push(item.sqiIr);
                  dailyData[dateKey].count++;
                }
              } else {
                // For other modalities, use sqi field
                if (item.sqi !== undefined && item.sqi !== null) {
                  const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                  dailyData[dateKey].signalQualityValues.push(sqiValue);
                  if (!dailyData[dateKey].originalValues) {
                    dailyData[dateKey].originalValues = [];
                  }
                  dailyData[dateKey].originalValues.push(item.sqi);
                  dailyData[dateKey].count++;
                }
              }
            }
          });
          
          // Convert to chart arrays (chronological order)
          if (index === 0) {
            chartLabels = Object.keys(dailyData).sort((a, b) => {
              return dailyData[a].date - dailyData[b].date;
            });
          }
          
          chartData = chartLabels.map(label => {
            const dayData = dailyData[label];
            if (dayData && dayData.count > 0) {
              const averageSignalQuality = dayData.signalQualityValues.reduce((sum, level) => sum + level, 0) / dayData.count;
              return Math.round(averageSignalQuality);
            }
            return null; // No data for this day - will show as gap
          });
        }
      }
      
      // Skip Blood Pressure modality
      if (modality.modalityType === 'bp') {
        return;
      }
      
      // Add dataset for this modality type
      const displayName = {
        'ecg': 'ECG',
        'ausc': 'Auscultation', 
        'bioz': 'Bioimpedance',
        'motion': 'Motion',
        'ppg': 'PPG'
      }[modality.modalityType] || modality.modalityType.toUpperCase();
      
      datasets.push({
        label: displayName,
        data: chartData.map(item => item && item.value !== undefined ? item.value : item),
        borderColor: color.border,
        backgroundColor: color.background,
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: color.point,
        metadata: chartData // Store original data for tooltip
      });
    });
    
    return {
      labels: chartLabels,
      datasets: datasets
    };
  };

  // Process battery data based on current view
  const processBatteryData = (rawData, view) => {
    if (!rawData || rawData.length === 0) {
      return {
            labels: [],
            datasets: [{
              label: 'Battery Level',
              data: [],
              borderColor: '#1D5769',
              backgroundColor: 'rgba(29, 87, 105, 0.1)',
              fill: true,
              tension: 0.4,
              borderWidth: 2,
              pointRadius: 4,
              pointBackgroundColor: '#1D5769',
            }]
      };
    }

    // Sort the data by date to ensure proper chronological order
    const sortedData = rawData.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
    
    let chartLabels = [];
    let chartData = [];
    
    if (view === 'hourly') {
      // For hourly view, show data for the last hour
      const now = new Date();
      const lastHour = new Date(now.getTime() - 60 * 60 * 1000);
      
      // Filter data to last hour
      const recentData = sortedData.filter(item => new Date(item.createdDate) >= lastHour);
      
      // If we have any data, create chart
      if (recentData.length > 0) {
        // Create labels and data from actual readings
        chartLabels = recentData.map(item => {
          const date = new Date(item.createdDate);
          return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        });
        
        chartData = recentData.map(item => Math.round(item.batteryLevel));
      } else {
        // No data in last hour
        chartLabels = [];
        chartData = [];
      }
      
    } else if (view === 'daily') {
      // For daily view, show hourly data for the last 24 hours
      const now = new Date();
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      // Filter data to last 24 hours
      const recentData = sortedData.filter(item => new Date(item.createdDate) >= last24Hours);
      
      // Create hourly buckets
      const hourlyData = {};
      for (let i = 0; i < 24; i++) {
        const hour = new Date(now.getTime() - i * 60 * 60 * 1000);
        const hourKey = hour.toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true
        });
        hourlyData[hourKey] = {
          time: hour,
          batteryLevels: [],
          count: 0
        };
      }
      
      // Group data by hour
      recentData.forEach(item => {
        const itemTime = new Date(item.createdDate);
        const hourKey = itemTime.toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true
        });
        if (hourlyData[hourKey]) {
          hourlyData[hourKey].batteryLevels.push(item.batteryLevel);
          hourlyData[hourKey].count++;
        }
      });
      
      // Convert to chart arrays (reverse order to show oldest to newest)
      // Show all 24 hours, including gaps with no data
      chartLabels = Object.keys(hourlyData).reverse();
      chartData = chartLabels.map(label => {
        const hourData = hourlyData[label];
        if (hourData.count > 0) {
          const averageBatteryLevel = hourData.batteryLevels.reduce((sum, level) => sum + level, 0) / hourData.count;
          return Math.round(averageBatteryLevel);
        }
        return null; // No data for this hour - will show as gap
      });
      
    } else if (view === 'weekly') {
      // For weekly view, show all 7 days including gaps
      const now = new Date();
      const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const recentData = sortedData.filter(item => new Date(item.createdDate) >= last7Days);
      
      // Create daily buckets for all 7 days
      const dailyData = {};
      for (let i = 0; i < 7; i++) {
        const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayKey = day.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        dailyData[dayKey] = {
          date: day,
          batteryLevels: [],
          count: 0
        };
      }
      
      // Group data by day
      recentData.forEach(item => {
        const dateKey = new Date(item.createdDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        
        if (dailyData[dateKey]) {
          dailyData[dateKey].batteryLevels.push(item.batteryLevel);
          dailyData[dateKey].count++;
        }
      });
      
      // Convert to chart arrays (reverse order to show oldest to newest)
      chartLabels = Object.keys(dailyData).reverse();
      chartData = chartLabels.map(label => {
        const dayData = dailyData[label];
        if (dayData.count > 0) {
          const averageBatteryLevel = dayData.batteryLevels.reduce((sum, level) => sum + level, 0) / dayData.count;
          return Math.round(averageBatteryLevel);
        }
        return null; // No data for this day - will show as gap
      });
      
    } else if (view === 'monthly') {
      // For monthly view, show all 30 days including gaps
      const now = new Date();
      const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const recentData = sortedData.filter(item => new Date(item.createdDate) >= last30Days);
      
      // Create daily buckets for all 30 days
      const dailyData = {};
      for (let i = 0; i < 30; i++) {
        const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayKey = day.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        dailyData[dayKey] = {
          date: day,
          batteryLevels: [],
          count: 0
        };
      }
      
      // Group data by day
      recentData.forEach(item => {
        const dateKey = new Date(item.createdDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        
        if (dailyData[dateKey]) {
          dailyData[dateKey].batteryLevels.push(item.batteryLevel);
          dailyData[dateKey].count++;
        }
      });
      
      // Convert to chart arrays (reverse order to show oldest to newest)
      chartLabels = Object.keys(dailyData).reverse();
      chartData = chartLabels.map(label => {
        const dayData = dailyData[label];
        if (dayData.count > 0) {
          const averageBatteryLevel = dayData.batteryLevels.reduce((sum, level) => sum + level, 0) / dayData.count;
          return Math.round(averageBatteryLevel);
        }
        return null; // No data for this day - will show as gap
      });
      
    } else if (view === 'all') {
      // For all view, show all days from first to last data point including gaps
      if (sortedData.length === 0) {
        chartLabels = [];
        chartData = [];
      } else {
        // Find the date range from first to last data point
        const firstDate = new Date(sortedData[0].createdDate);
        const lastDate = new Date(sortedData[sortedData.length - 1].createdDate);
        
        // Create daily buckets for all days in the range
        const dailyData = {};
        const currentDate = new Date(firstDate);
        while (currentDate <= lastDate) {
          const dateKey = currentDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          dailyData[dateKey] = {
            date: new Date(currentDate),
            batteryLevels: [],
            count: 0
          };
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // Group data by day
        sortedData.forEach(item => {
          const dateKey = new Date(item.createdDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          
          if (dailyData[dateKey]) {
            dailyData[dateKey].batteryLevels.push(item.batteryLevel);
            dailyData[dateKey].count++;
          }
        });
        
        // Convert to chart arrays (chronological order)
        chartLabels = Object.keys(dailyData).sort((a, b) => {
          return dailyData[a].date - dailyData[b].date;
        });
        
        chartData = chartLabels.map(label => {
          const dayData = dailyData[label];
          if (dayData.count > 0) {
            const averageBatteryLevel = dayData.batteryLevels.reduce((sum, level) => sum + level, 0) / dayData.count;
            return Math.round(averageBatteryLevel);
          }
          return null; // No data for this day - will show as gap
        });
      }
    }
    
    return {
      labels: chartLabels,
          datasets: [{
            label: 'Battery Level',
        data: chartData,
            borderColor: '#1D5769',
            backgroundColor: 'rgba(29, 87, 105, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: '#1D5769',
          }]
    };
  };

  // Fetch battery levels data (only when patient ID changes)
  useEffect(() => {
    const fetchBatteryLevels = async () => {
      if (!patientInfo.id) return;

      try {
        const response = await httpService.get(`/measurements/patch/metadata/${patientInfo.id}`);
        
        if ((response.data?.success || response.data?.code === 200) && response.data?.data && Array.isArray(response.data.data)) {
          // Convert metadata array response to battery levels format for compatibility
          const batteryLevelData = response.data.data.map(item => ({
            batteryLevel: Math.round((item.batteryLevel || 0) * 100), // Convert decimal to percentage (0.87 -> 87)
            createdDate: item.createdDate || new Date().toISOString()
          }));
          setRawBatteryData(batteryLevelData);
        } else {
          setRawBatteryData([]);
        }
      } catch (err) {
        console.error('Error fetching battery levels:', err);
        setRawBatteryData([]);
      }
    };

    fetchBatteryLevels();
  }, [patientInfo.id, httpService]);

  // Fetch signal quality data (only when patient ID changes)
  useEffect(() => {
    const fetchSignalQualityData = async () => {
      if (!patientInfo.id) return;

      try {
        const response = await httpService.get(`/device/sqi-readings/${patientInfo.id}`);
        
        if ((response.data?.success || response.data?.code === 200) && response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          setRawSignalQualityData(response.data.data);
        } else {
          setRawSignalQualityData([]);
        }
      } catch (err) {
        console.error('Error fetching signal quality data:', err);
        setRawSignalQualityData([]);
      }
    };

    fetchSignalQualityData();
  }, [patientInfo.id, httpService]);

  // Fetch alerts data (only when patient ID changes)
  useEffect(() => {
    const fetchAlertsData = async () => {
      if (!patientInfo.id) return;

      try {
        const response = await httpService.get(`/alerts/${patientInfo.id}?deviceStatus=true`);
        
        if ((response.data?.success || response.data?.code === 200) && response.data?.data && Array.isArray(response.data.data)) {
          setAlertsData(response.data.data);
        } else {
          setAlertsData([]);
        }
      } catch (err) {
        console.error('Error fetching alerts data:', err);
        setAlertsData([]);
      }
    };

    fetchAlertsData();
  }, [patientInfo.id, httpService]);

  // Fetch health patch metadata (hardware/software versions and reboot tracking)
  useEffect(() => {
    const fetchPatchMetadata = async () => {
      if (!patientInfo.id) return;

      try {
        // Fetch full metadata array to analyze reboot history
        const metadataArrayResponse = await httpService.get(`/measurements/patch/metadata/${patientInfo.id}`);
        
        if ((metadataArrayResponse.data?.success || metadataArrayResponse.data?.code === 200) && 
            metadataArrayResponse.data?.data && 
            Array.isArray(metadataArrayResponse.data.data) && 
            metadataArrayResponse.data.data.length > 0) {
            
            const metadataArray = metadataArrayResponse.data.data;
            
            // Sort by createdDate descending to get most recent first
            const sortedMetadata = [...metadataArray].sort((a, b) => 
                new Date(b.createdDate) - new Date(a.createdDate)
            );
            
            // Use the latest metadata for device schedule and display
            const latestMetadata = sortedMetadata[0];
            setPatchMetadata(latestMetadata);
            
            // Process reboot count
            const currentRebootCount = latestMetadata.rebootCount || null;
            setRebootCount(currentRebootCount);
            
            // Find when the reboot count last increased
            let rebootTimestamp = null;
            if (sortedMetadata.length > 1 && currentRebootCount !== null) {
                // Look through sorted metadata to find where reboot count changed
                for (let i = 0; i < sortedMetadata.length - 1; i++) {
                    const currentCount = sortedMetadata[i].rebootCount || 0;
                    const previousCount = sortedMetadata[i + 1].rebootCount || 0;
                    
                    if (currentCount > previousCount) {
                        // Found where reboot count increased
                        rebootTimestamp = sortedMetadata[i].createdDate;
                        break;
                    }
                }
                
                // If no increase found, use the latest metadata timestamp
                if (!rebootTimestamp) {
                    rebootTimestamp = latestMetadata.createdDate;
                }
            } else {
                // Single metadata entry or no reboot count, use latest timestamp
                rebootTimestamp = latestMetadata.createdDate || null;
            }
            
            setLastRebootTimestamp(rebootTimestamp);
        } else {
            // If array is empty, try fetching latest metadata as fallback
            const latestMetadataResponse = await httpService.get(`/measurements/patch/metadata/latest/${patientInfo.id}`);
            if ((latestMetadataResponse.data?.success || latestMetadataResponse.data?.code === 200) && latestMetadataResponse.data?.data) {
                const latestData = latestMetadataResponse.data.data;
                setPatchMetadata(latestData);
                setRebootCount(latestData.rebootCount || null);
                setLastRebootTimestamp(latestData.createdDate || null);
            } else {
                setPatchMetadata(null);
                setRebootCount(null);
                setLastRebootTimestamp(null);
            }
        }
      } catch (err) {
        console.error('Error fetching patch metadata:', err);
        setPatchMetadata(null);
        setRebootCount(null);
        setLastRebootTimestamp(null);
      }
    };

    fetchPatchMetadata();
  }, [patientInfo.id, httpService]);

  // Fetch latest recording timestamp and calculate on-track status
  useEffect(() => {
    const fetchAndCalculateTrackingData = async () => {
      if (!patientInfo.id) return;
      
      // Create a key to track if we've already made this exact call
      const callKey = `${patientInfo.id}-${patchMetadata?.deviceSchedule ? 'with-schedule' : 'no-schedule'}`;
      
      // Skip if we've already made this exact call
      if (lastTrackingCallRef.current === callKey) {
        return;
      }
      
      lastTrackingCallRef.current = callKey;
      
      const { latestTimestamp, isOnTrack } = await fetchDeviceTrackingData(
        patientInfo.id,
        httpService,
        patchMetadata
      );

      if (latestTimestamp) {
        setLatestRecordingTimestamp(latestTimestamp);
      }
      setIsDeviceOnTrack(isOnTrack);
    };

    fetchAndCalculateTrackingData();
  }, [patientInfo.id, httpService, patchMetadata]);

  // Process battery data when raw data or view changes
  useEffect(() => {
    if (rawBatteryData !== null) {
      const processedData = processBatteryData(rawBatteryData, batteryChartView);
      setBatteryData(processedData);
      
      // Fallback: If we have battery data but no active patch detected, 
      // consider it as having an active patch
      if (rawBatteryData.length > 0 && !hasActivePatch) {
        setHasActivePatch(true);
      }
    }
  }, [rawBatteryData, batteryChartView, hasActivePatch]);

  // Process signal quality data when raw data or view changes
  useEffect(() => {
    if (rawSignalQualityData !== null) {
      const processedData = processSignalQualityData(rawSignalQualityData, signalQualityChartView);
      setSignalQualityData(processedData);
    }
  }, [rawSignalQualityData, signalQualityChartView]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };


  // Use real battery data from API - no fallback to mock data
  const batteryHistoryData = batteryData || {
    labels: [],
    datasets: [
      {
        label: 'Battery Level',
        data: [],
        borderColor: '#1D5769',
        backgroundColor: 'rgba(29, 87, 105, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#1D5769',
      }
    ]
  };

  // Use real signal quality data from API
  const signalQualityHistoryData = signalQualityData || {
    labels: [],
    datasets: [
      {
        label: 'Signal Quality',
        data: [],
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#28a745',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 10,
        right: 10
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: {
            size: 14,
            weight: 'bold'
          },
          padding: 20
        },
        padding: {
          top: 60,
          bottom: 20
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            // Check if this is signal quality data (has multiple datasets) or battery data
            if (context.dataset.label.includes('ECG') || context.dataset.label.includes('Signal Quality') || 
                context.dataset.label.includes('Auscultation') || context.dataset.label.includes('Blood Pressure') ||
                context.dataset.label.includes('Bioimpedance') || context.dataset.label.includes('Motion') ||
                context.dataset.label.includes('PPG')) {
              
              // Check if this point has invalid readings
              if (context.dataset.metadata && context.dataset.metadata[context.dataIndex]) {
                const metadata = context.dataset.metadata[context.dataIndex];
                if (metadata && metadata.hasInvalidReadings) {
                  return `${context.dataset.label}: Invalid Reading`;
                }
              }
              return `${context.dataset.label}: ${Math.round(context.parsed.y)}%`;
            }
            return `${context.dataset.label}: ${context.parsed.y}%`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        },
        ticks: {
          callback: value => `${value}%`,
          font: {
            size: 12
          },
          padding: 15
        },
        offset: true
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          padding: 8
        }
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'normal':
      case 'active':
        return '#28a745';
      case 'warning':
        return '#ffc107';
      case 'error':
      case 'critical':
        return '#dc3545';
      case 'low':
        return '#ffc107'; // Yellow for low priority
      case 'medium':
        return '#fd7e14'; // Orange for medium priority
      case 'high':
        return '#dc3545'; // Red for high priority
      default:
        return '#6c757d';
    }
  };

  // Get the latest battery reading from API data
  const getLatestBatteryReading = () => {
    if (!rawBatteryData || rawBatteryData.length === 0) {
      return null;
    }
    
    // Sort by createdDate descending to get the latest reading
    const sortedData = [...rawBatteryData].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
    return sortedData[0];
  };



  if (error) {
    return (
      <div className={styles.ViewContainer}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Device Information</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!deviceData) {
    return (
      <div className={styles.ViewContainer}>
      </div>
    );
  }

  return (
    <div className={styles.ViewContainer}>
       <div className={styles.PatientViewScrollContainer}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="mb-2">
              Health Patch
              <span className="ms-3">
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(isDeviceOnTrack ? 'Active' : 'Inactive'),
                  marginRight: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} />
                <span style={{ fontSize: '0.7em', fontWeight: 'bold' }}>
                  {isDeviceOnTrack ? 'On Track' : 'Off Track'}
                </span>
              </span>
            </h3>
            <div>
              <span className="me-3"><strong>Serial Number:</strong> {(patchMetadata?.serialNumber && patchMetadata.serialNumber !== 'string') ? patchMetadata.serialNumber : (deviceData?.serialNumber && deviceData.serialNumber !== 'string' ? deviceData.serialNumber : 'N/A')}</span>
              <span className="me-3"><strong>Mac Address:</strong> {deviceData.macAddress ?? "Unknown"}</span>
              <span className="me-3"><strong>Reboot Count:</strong> {rebootCount !== null ? rebootCount : 'N/A'}</span>
              {lastRebootTimestamp && (
                <span><strong>Latest Reboot:</strong> {formatDate(lastRebootTimestamp)}</span>
              )}
            </div>
            <div className="mt-2">
              <span className="me-3">
                <strong>Last Connection: </strong>{formatDate(patchMetadata?.createdDate || latestRecordingTimestamp || deviceData.lastConnection)}
              </span>
              <span className="me-3">
                <strong>Last Data Transmission: </strong>{formatDate((deviceData?.lastDataTransmissionTime ? deviceData.lastDataTransmissionTime : null) || latestRecordingTimestamp || patchMetadata?.createdDate)}
              </span>
              <span>
                <strong>Software Version: </strong>{(patchMetadata?.appSoftwareVersion && patchMetadata.appSoftwareVersion !== 'string') ? patchMetadata.appSoftwareVersion : (deviceData?.currentSoftwareVersion && deviceData.currentSoftwareVersion !== 'string' ? deviceData.currentSoftwareVersion : 'N/A')}
              </span>
            </div>
          </div>
        </div>

        <Row style={{width: '100%'}}>
          <Col lg={5}>
            {/* Battery Status */}
            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <Card.Title className="mb-3">Battery Status</Card.Title>
                {hasActivePatch ? (
                  (() => {
                    const latestBatteryReading = getLatestBatteryReading();
                    
                    if (!latestBatteryReading) {
                      return (
                        <div className="d-flex align-items-center mb-2">
                          <div style={{
                            width: '100%',
                            height: '24px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <div style={{
                              width: '0%',
                              height: '100%',
                              backgroundColor: '#6c757d',
                              transition: 'width 0.3s ease',
                              boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.2)'
                            }} />
                          </div>
                          <span className="ms-3 fw-bold" style={{ 
                            color: '#6c757d',
                            minWidth: '100px'
                          }}>
                            No Data
                          </span>
                        </div>
                      );
                    }

                    const batteryLevel = latestBatteryReading.batteryLevel;
                    const statusText = getBatteryStatusText(batteryLevel);
                    
                    return (
                      <div className="d-flex align-items-center mb-2">
                        <div style={{
                          width: '100%',
                          height: '24px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{
                            width: `${batteryLevel}%`,
                            height: '100%',
                            backgroundColor: getBatteryColor(batteryLevel),
                            transition: 'width 0.3s ease',
                            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.2)'
                          }} />
                        </div>
                        <span className="ms-3 fw-bold" style={{ 
                          color: getBatteryColor(batteryLevel),
                          minWidth: '100px'
                        }}>
                          {batteryLevel}% - {statusText}
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <div>No active patch</div>
                )}
              </Card.Body>
            </Card>

            {/* Active Errors */}
            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <Card.Title className="mb-3">Active Errors</Card.Title>
                {alertsData && alertsData.length > 0 ? (
                  (() => {
                    // Group alerts by alert_name
                    const groupedAlerts = alertsData.reduce((acc, alert) => {
                      const alertName = alert.alert_name || 'Unknown Alert';
                      if (!acc[alertName]) {
                        acc[alertName] = [];
                      }
                      acc[alertName].push(alert);
                      return acc;
                    }, {});

                    // Sort groups by the latest alert date (most recent groups first)
                    const sortedGroups = Object.entries(groupedAlerts).sort(([, alertsA], [, alertsB]) => {
                      const latestA = Math.max(...alertsA.map(a => {
                        const timestamp = a.alert_name === 'Lead Off' ? a.alert_lastUpdatedTimestamp : a.createdDate;
                        return new Date(timestamp || a.createdDate).getTime();
                      }));
                      const latestB = Math.max(...alertsB.map(a => {
                        const timestamp = a.alert_name === 'Lead Off' ? a.alert_lastUpdatedTimestamp : a.createdDate;
                        return new Date(timestamp || a.createdDate).getTime();
                      }));
                      return latestB - latestA;
                    });

                    // Render each group
                    return sortedGroups.map(([alertName, alerts]) => {
                      // Sort alerts by date descending (latest first) - use alert_lastUpdatedTimestamp for Lead Off
                      const sortedAlerts = alerts.sort((a, b) => {
                        const timestampA = a.alert_name === 'Lead Off' ? (a.alert_lastUpdatedTimestamp || a.createdDate) : a.createdDate;
                        const timestampB = b.alert_name === 'Lead Off' ? (b.alert_lastUpdatedTimestamp || b.createdDate) : b.createdDate;
                        return new Date(timestampB) - new Date(timestampA);
                      });
                      const latestAlert = sortedAlerts[0];
                      const hasMultiple = sortedAlerts.length > 1;
                      const groupKey = `group-${alertName}`;
                      const isGroupExpanded = expandedAlerts[groupKey];
                      
                      return (
                        <div key={groupKey} className="mb-4">
                          <div className="d-flex align-items-center mb-2">
                            <span style={{
                              display: 'inline-block',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: getStatusColor(latestAlert.alert_priority),
                              marginRight: '12px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }} />
                            <strong className="me-2">{alertName}</strong>
                            {hasMultiple && (
                              <span className="badge bg-secondary me-2">{sortedAlerts.length}</span>
                            )}
                            <span className="text-muted" style={{ fontSize: '0.9em' }}>
                              ({formatDate(latestAlert.alert_name === 'Lead Off' ? (latestAlert.alert_lastUpdatedTimestamp || latestAlert.createdDate) : latestAlert.createdDate)})
                            </span>
                            {hasMultiple && (
                              <button
                                onClick={() => setExpandedAlerts(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#1D5769',
                                  cursor: 'pointer',
                                  fontSize: '0.85em',
                                  marginLeft: 'auto',
                                  padding: '0',
                                  fontWeight: 'bold'
                                }}
                              >
                                {isGroupExpanded ? 'Collapse' : 'Expand'}
                              </button>
                            )}
                          </div>
                          <p className="mb-3 ms-4">{latestAlert.alert_description}</p>
                          
                          {/* Show older alerts if expanded */}
                          {hasMultiple && (
                            <Collapse in={isGroupExpanded}>
                              <div className="ms-4 mb-3">
                                <div className="bg-light p-3 rounded">
                                  <strong className="d-block mb-2">Previous Occurrences:</strong>
                                  {sortedAlerts.slice(1).map((alert, idx) => (
                                    <div key={alert.alertId || idx} className="mb-2 pb-2" style={{ borderBottom: idx < sortedAlerts.length - 2 ? '1px solid #dee2e6' : 'none' }}>
                                      <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                        {formatDate(alert.alert_name === 'Lead Off' ? (alert.alert_lastUpdatedTimestamp || alert.createdDate) : alert.createdDate)}
                                      </div>
                                      <div style={{ fontSize: '0.9em' }}>{alert.alert_description}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Collapse>
                          )}
                          
                          <div className="ms-4">
                            <button
                              onClick={() => setExpandedAlerts(prev => ({ ...prev, [`details-${groupKey}`]: !prev[`details-${groupKey}`] }))}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#1D5769',
                                cursor: 'pointer',
                                fontSize: '0.9em',
                                fontWeight: '500',
                                padding: '4px 0',
                                marginBottom: '8px'
                              }}
                            >
                              {expandedAlerts[`details-${groupKey}`] ? 'Hide' : 'Show'} Alert Details
                            </button>
                            <Collapse in={expandedAlerts[`details-${groupKey}`]}>
                              <div className="bg-light p-3 rounded">
                                <div className="row">
                                  <div className="col-md-6">
                                    <strong className="d-block mb-2">Alert Details:</strong>
                                    <ul className="mb-0 ps-3">
                                      <li><strong>Category:</strong> {latestAlert.alert_category}</li>
                                      <li><strong>Priority:</strong> {latestAlert.alert_priority}</li>
                                      <li><strong>Status:</strong> {latestAlert.alert_status}</li>
                                      <li><strong>Device ID:</strong> {latestAlert.alert_deviceId}</li>
                                    </ul>
                                  </div>
                                  <div className="col-md-6">
                                    <strong className="d-block mb-2">Additional Info:</strong>
                                    <ul className="mb-0 ps-3">
                                      <li><strong>Escalation:</strong> {latestAlert.alert_escalation}</li>
                                      <li><strong>SMS Sent:</strong> {latestAlert.alert_sms ? 'Yes' : 'No'}</li>
                                      <li><strong>Push Notification:</strong> {latestAlert.alert_push_notification ? 'Yes' : 'No'}</li>
                                      {latestAlert.alert_trigger_count && (
                                        <li><strong>Trigger Count:</strong> {latestAlert.alert_trigger_count}</li>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </Collapse>
                          </div>
                        </div>
                      );
                    });
                  })()
                ) : (
                  <p className="text-muted mb-0">No active errors</p>
                )}
              </Card.Body>
            </Card>





          </Col>

          <Col lg={7}>
            {/* Battery Level History Chart */}
            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Card.Title className="mb-0">Battery Level History</Card.Title>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${batteryChartView === 'hourly' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setBatteryChartView('hourly')}
                    >
                      Hourly
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${batteryChartView === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setBatteryChartView('daily')}
                    >
                      Daily
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${batteryChartView === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setBatteryChartView('weekly')}
                    >
                      Weekly
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${batteryChartView === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setBatteryChartView('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${batteryChartView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setBatteryChartView('all')}
                    >
                      All
                    </button>
                  </div>
                </div>
                <div style={{ height: '350px' }}>
                  {batteryHistoryData.labels.length > 0 && batteryHistoryData.datasets[0].data.some(value => value !== null) ? (
                    <Line data={batteryHistoryData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                      <div className="text-center">
                        <p className="mb-0">
                          {rawBatteryData && rawBatteryData.length > 0 
                            ? "No battery level data available for this time frame"
                            : "No battery level data available"
                          }
                        </p>
                        <small>
                          {rawBatteryData && rawBatteryData.length > 0 
                            ? "Try selecting a different time frame to view data"
                            : "Data will appear here once the device starts transmitting"
                          }
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Signal Quality Chart */}
            <Card className="shadow-sm">
              <Card.Body style={{ paddingBottom: '20px' }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Card.Title className="mb-0">Signal Quality History</Card.Title>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${signalQualityChartView === 'hourly' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSignalQualityChartView('hourly')}
                    >
                      Hourly
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${signalQualityChartView === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSignalQualityChartView('daily')}
                    >
                      Daily
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${signalQualityChartView === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSignalQualityChartView('weekly')}
                    >
                      Weekly
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${signalQualityChartView === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSignalQualityChartView('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${signalQualityChartView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSignalQualityChartView('all')}
                    >
                      All
                    </button>
                  </div>
                </div>
                <div style={{ height: '500px' }}>
                  {signalQualityHistoryData.labels.length > 0 && signalQualityHistoryData.datasets.length > 0 && signalQualityHistoryData.datasets.some(dataset => dataset.data.some(value => value !== null)) ? (
                    <Line data={signalQualityHistoryData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                      <div className="text-center">
                        <p className="mb-0">
                          {rawSignalQualityData && rawSignalQualityData.length > 0 
                            ? "No signal quality data available for this time frame"
                            : "No signal quality data available"
                          }
                        </p>
                        <small>
                          {rawSignalQualityData && rawSignalQualityData.length > 0 
                            ? "Try selecting a different time frame to view data"
                            : "Data will appear here once the device starts transmitting"
                          }
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DeviceInfoView; 